import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HammerIcon, WrenchIcon, LeafIcon } from './icons';
import { useLanguage } from '../context/LanguageContext';
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0
  })
};
export function ServicePresentation() {
  const {
    t
  } = useLanguage();
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const tabs = [{
    key: 'remodeling',
    titleKey: 'services.remodeling.title',
    itemsKey: 'services.remodeling.items',
    icon: HammerIcon,
    headerBg: 'bg-brand-green-dark',
    headerBorder: 'border-brand-green',
    cardBorder: 'border-brand-green/30',
    shimmer: 'shimmer-border shimmer-border-green'
  }, {
    key: 'restoration',
    titleKey: 'services.restoration.title',
    itemsKey: 'services.restoration.items',
    icon: WrenchIcon,
    headerBg: 'bg-brand-gold-dark',
    headerBorder: 'border-brand-gold',
    cardBorder: 'border-brand-gold/30',
    shimmer: 'shimmer-border'
  }, {
    key: 'gardening',
    titleKey: 'services.gardening.title',
    itemsKey: 'services.gardening.items',
    icon: LeafIcon,
    headerBg: 'bg-brand-green-dark',
    headerBorder: 'border-brand-green',
    cardBorder: 'border-brand-green/30',
    shimmer: 'shimmer-border shimmer-border-green'
  }];
  const goTo = (index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };
  const next = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % tabs.length);
  };
  const prev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + tabs.length) % tabs.length);
  };
  const activeTab = tabs[current];
  const items = t(activeTab.itemsKey);
  const Icon = activeTab.icon;
  return <section id="servicios" className="py-12 bg-brand-bg-light" data-id="element-287">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-288">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-289">
          <div className="text-center mb-8" data-id="element-290">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white mb-4 tracking-wide" data-id="element-291">
              {t('services.title')}
            </h2>
            <p className="text-xl sm:text-2xl text-brand-gold font-medium mb-2" data-id="element-292">
              {t('services.subtitle')}
            </p>
            <p className="text-lg text-gray-400" data-id="element-293">{t('services.description')}</p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center gap-2 sm:gap-3 mb-8" data-id="element-294">
            {tabs.map((tab, index) => {
            const TabIcon = tab.icon;
            return <button key={tab.key} onClick={() => goTo(index)} className={`flex items-center gap-2 px-4 py-2.5 sm:px-6 sm:py-3 rounded-full font-heading font-bold text-xs sm:text-sm uppercase tracking-wider transition-all duration-300 border ${index === current ? 'bg-brand-gold text-black border-brand-gold shadow-lg shadow-brand-gold/20' : 'bg-brand-bg-dark text-gray-400 border-gray-700 hover:text-white hover:border-gray-500'}`} data-id="element-295">
                  <TabIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-296" />
                  <span className="hidden sm:inline" data-id="element-297">
                    {t(tab.titleKey).replace('SERVICIOS DE ', '').replace(' SERVICES', '')}
                  </span>
                  <span className="sm:hidden" data-id="element-298">
                    {t(tab.titleKey).replace('SERVICIOS DE ', '').replace(' SERVICES', '').split(' ')[0]}
                  </span>
                </button>;
          })}
          </div>

          {/* Slide Container */}
          <div className="relative" data-id="element-299">
            {/* Prev / Next Arrows */}
            <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-6 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors" aria-label="Previous" data-id="element-300">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-301">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" data-id="element-302" />
              </svg>
            </button>
            <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-6 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors" aria-label="Next" data-id="element-303">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-304">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-305" />
              </svg>
            </button>

            <div className="overflow-hidden" data-id="element-306">
              <AnimatePresence custom={direction} mode="wait" data-id="element-307">
                <motion.div key={current} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} className={`bg-brand-bg-dark rounded-2xl ${activeTab.cardBorder} border overflow-hidden shadow-xl ${activeTab.shimmer}`} data-id="element-308">
                  <div className={`${activeTab.headerBg} p-6 flex items-center justify-center gap-4 ${activeTab.headerBorder} border-b`} data-id="element-309">
                    <Icon className="w-8 h-8 text-white" data-id="element-310" />
                    <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white m-0" data-id="element-311">
                      {t(activeTab.titleKey)}
                    </h3>
                  </div>
                  <div className="p-0" data-id="element-312">
                    <table className="w-full text-left border-collapse" data-id="element-313">
                      <tbody data-id="element-314">
                        {Array.isArray(items) && items.map((item: any, index: number) => <motion.tr key={index} initial={{
                        opacity: 0,
                        x: -20
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        duration: 0.3,
                        delay: index * 0.05
                      }} className="border-b border-gray-800 hover:bg-white/5 transition-colors" data-id="element-315">
                              <td className="py-4 px-6 font-heading font-bold text-brand-gold text-lg w-1/3 align-top" data-id="element-316">
                                {item.name}
                              </td>
                              <td className="py-4 px-6 text-gray-300" data-id="element-317">
                                {item.desc}
                              </td>
                            </motion.tr>)}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-6" data-id="element-318">
            {tabs.map((_, index) => <button key={index} onClick={() => goTo(index)} className={`w-3 h-3 rounded-full transition-all duration-300 ${index === current ? 'bg-brand-gold w-8' : 'bg-gray-600 hover:bg-gray-400'}`} aria-label={`Go to service ${index + 1}`} data-id="element-319" />)}
          </div>
        </motion.div>
      </div>
    </section>;
}